import { useEffect, useRef, useState } from 'react';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { listPaymentSessionsFromConnect } from '@/lib/connect-api';

interface UsePollForSessionOptions {
  enabled: boolean;
  sessionId: string | null;
  /** Runs once when the session completes, and once on timeout — see the timeout note below. */
  onSessionComplete: () => Promise<void>;
  intervalMs?: number;
  maxDurationMs?: number;
  getAccessToken?: () => Promise<string | null>;
}

async function checkSessionCompleted(sessionId: string, getAccessToken?: () => Promise<string | null>): Promise<boolean> {
  const authStage = (localStorage.getItem(STORAGE_KEYS.AUTH_STAGE) as 'limited' | 'full' | null) ?? 'limited';
  const sessions = await listPaymentSessionsFromConnect({ id: sessionId, statuses: ['completed'] }, authStage, getAccessToken);
  return sessions.length > 0;
}

export function usePollForSession({
  enabled,
  sessionId,
  onSessionComplete,
  intervalMs = 3000,
  maxDurationMs = 120_000,
  getAccessToken,
}: UsePollForSessionOptions) {
  const [isPending, setIsPending] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isPollingRef = useRef(false);
  const startTimeRef = useRef<number>(0);
  const mountedRef = useRef(true);
  const onSessionCompleteRef = useRef(onSessionComplete);
  onSessionCompleteRef.current = onSessionComplete;
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    if (!enabled || !sessionId) return;

    mountedRef.current = true;
    setIsPending(true);
    startTimeRef.current = Date.now();

    const id = setInterval(async () => {
      if (isPollingRef.current) return;
      isPollingRef.current = true;

      try {
        const found = await checkSessionCompleted(sessionId, getAccessTokenRef.current);
        const timedOut = Date.now() - startTimeRef.current > maxDurationMs;

        if (!mountedRef.current) return;

        if (found || timedOut) {
          clearInterval(id);
          // Refetch on timeout too: the caller skipped its own fetch on the way in
          // because this poll owns the refresh, so giving up without one would leave
          // pre-payment figures on screen until the customer reloaded.
          await onSessionCompleteRef.current();
          if (mountedRef.current) {
            if (timedOut && !found) setIsTimedOut(true);
            setIsPending(false);
          }
        }
      } catch (err) {
        console.error('[usePollForSession] Poll error:', err);
      } finally {
        isPollingRef.current = false;
      }
    }, intervalMs);

    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [enabled, sessionId]);

  return { isPending, isTimedOut };
}
